import React, { useState, useCallback } from "react";
import { RendererProps } from "./index";
import { ColumnDef } from "../../../context/questionnaire/types";
import {
  ChoiceList,
  NestedPanel,
  NestedPanelHeader,
  NestedPanelBody,
  EntryCardRow,
  SelectWrapper,
  TextInput,
  FieldLabel,
  AddButton,
  RemoveButton,
} from "./renderer-styles";

interface NestedRow {
  values: Record<string, any>;
  is_valid_row: boolean;
  required_fields?: Record<string, boolean>;
}

interface MainRow {
  values: Record<string, any>;
  nested_rows: NestedRow[];
  is_valid_row: boolean;
  required_fields?: Record<string, boolean>;
}

function emptyRow(cols: ColumnDef[]): Record<string, any> {
  const v: Record<string, any> = {};
  cols.forEach((c) => {
    v[c.name] = "";
  });
  return v;
}

function isRowFilled(values: Record<string, any>, cols: ColumnDef[]): boolean {
  return cols
    .filter((c) => c.required && c.type !== "remove_button")
    .every((c) => {
      const v = values[c.name];
      return v !== undefined && v !== "" && v !== null;
    });
}

function initMainRows(
  answer: RendererProps["answer"],
  tierOne: ColumnDef[],
  tierTwo: ColumnDef[]
): MainRow[] {
  if (answer?.value && Array.isArray(answer.value) && answer.value.length > 0) {
    return answer.value.map((r: any) => ({
      values: r.values || {},
      nested_rows: (r.nested_rows || []).map((nr: any) => ({
        values: nr.values || {},
        is_valid_row: nr.is_valid_row ?? false,
        required_fields: nr.required_fields,
      })),
      is_valid_row: r.is_valid_row ?? false,
      required_fields: r.required_fields,
    }));
  }
  return [
    {
      values: emptyRow(tierOne),
      nested_rows: [{ values: emptyRow(tierTwo), is_valid_row: false }],
      is_valid_row: false,
    },
  ];
}

function renderColumnInput(
  col: ColumnDef,
  value: any,
  onChange: (val: any) => void
) {
  switch (col.type) {
    case "select":
    case "radio":
      return (
        <SelectWrapper
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">—</option>
          {(col.options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.title || o.label}
            </option>
          ))}
        </SelectWrapper>
      );
    case "number":
      return (
        <TextInput
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
        />
      );
    case "date":
      return (
        <TextInput
          type="date"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "remove_button":
      return null;
    default:
      return (
        <TextInput
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={col.title || ""}
        />
      );
  }
}

const MultiColumnNestedRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {

  const tierOne = question.columns_tier_one || [];
  const tierTwo = question.columns_tier_two || [];

  const [rows, setRows] = useState<MainRow[]>(() =>
    initMainRows(answer, tierOne, tierTwo)
  );
  const [expandedIdx, setExpandedIdx] = useState<number | null>(0);

  const emit = useCallback(
    (nextRows: MainRow[]) => {
      const tagged = nextRows.map((r) => ({
        ...r,
        is_valid_row: isRowFilled(r.values, tierOne),
        nested_rows: r.nested_rows.map((nr) => ({
          ...nr,
          is_valid_row: isRowFilled(nr.values, tierTwo),
        })),
      }));
      const hasValid = tagged.some((r) => r.is_valid_row);
      onAnswer(tagged, hasValid);
      return tagged;
    },
    [onAnswer, tierOne, tierTwo]
  );

  const updateMainValue = useCallback(
    (rowIdx: number, colName: string, val: any) => {
      setRows((prev) => {
        const next = prev.map((r, i) =>
          i === rowIdx ? { ...r, values: { ...r.values, [colName]: val } } : r
        );
        return emit(next);
      });
    },
    [emit]
  );

  const updateNestedValue = useCallback(
    (rowIdx: number, nestedIdx: number, colName: string, val: any) => {
      setRows((prev) => {
        const next = prev.map((r, i) => {
          if (i !== rowIdx) return r;
          const nrs = r.nested_rows.map((nr, ni) =>
            ni === nestedIdx
              ? { ...nr, values: { ...nr.values, [colName]: val } }
              : nr
          );
          return { ...r, nested_rows: nrs };
        });
        return emit(next);
      });
    },
    [emit]
  );

  const addMainRow = useCallback(() => {
    setRows((prev) => {
      const next = [
        ...prev,
        {
          values: emptyRow(tierOne),
          nested_rows: [{ values: emptyRow(tierTwo), is_valid_row: false }],
          is_valid_row: false,
        },
      ];
      setExpandedIdx(next.length - 1);
      return emit(next);
    });
  }, [emit, tierOne, tierTwo]);

  const removeMainRow = useCallback(
    (idx: number) => {
      setRows((prev) => {
        const next = prev.filter((_, i) => i !== idx);
        if (next.length === 0) {
          const fresh = [
            {
              values: emptyRow(tierOne),
              nested_rows: [{ values: emptyRow(tierTwo), is_valid_row: false }],
              is_valid_row: false,
            },
          ];
          setExpandedIdx(0);
          return emit(fresh);
        }
        if (expandedIdx !== null && expandedIdx >= next.length) {
          setExpandedIdx(next.length - 1);
        }
        return emit(next);
      });
    },
    [emit, tierOne, tierTwo, expandedIdx]
  );

  const addNestedRow = useCallback(
    (rowIdx: number) => {
      setRows((prev) => {
        const next = prev.map((r, i) => {
          if (i !== rowIdx) return r;
          return {
            ...r,
            nested_rows: [
              ...r.nested_rows,
              { values: emptyRow(tierTwo), is_valid_row: false },
            ],
          };
        });
        return emit(next);
      });
    },
    [emit, tierTwo]
  );

  const removeNestedRow = useCallback(
    (rowIdx: number, nestedIdx: number) => {
      setRows((prev) => {
        const next = prev.map((r, i) => {
          if (i !== rowIdx) return r;
          const nrs = r.nested_rows.filter((_, ni) => ni !== nestedIdx);
          return {
            ...r,
            nested_rows:
              nrs.length === 0
                ? [{ values: emptyRow(tierTwo), is_valid_row: false }]
                : nrs,
          };
        });
        return emit(next);
      });
    },
    [emit, tierTwo]
  );

  const summarize = (row: MainRow): string => {
    const parts = tierOne
      .filter((c) => c.type !== "remove_button")
      .map((c) => {
        const v = row.values[c.name];
        if (!v) return null;
        const opt = c.options?.find((o) => o.value === v);
        return opt ? opt.title || opt.label : v;
      })
      .filter(Boolean);
    return parts.join(" — ") || "New entry";
  };

  return (
    <ChoiceList>
      {rows.map((row, idx) => (
        <NestedPanel key={idx} $expanded={expandedIdx === idx}>
          <NestedPanelHeader
            role="button"
            tabIndex={0}
            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpandedIdx(expandedIdx === idx ? null : idx); } }}
          >
            <span>{summarize(row)}</span>
            <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {rows.length > 1 && (
                <RemoveButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeMainRow(idx);
                  }}
                  aria-label="Remove entry"
                  style={{ minWidth: 36, minHeight: 36, fontSize: 14 }}
                >
                  ✕
                </RemoveButton>
              )}
              <span>{expandedIdx === idx ? "▲" : "▼"}</span>
            </span>
          </NestedPanelHeader>

          {expandedIdx === idx && (
            <NestedPanelBody>
              <EntryCardRow>
                {tierOne
                  .filter((c) => c.type !== "remove_button")
                  .map((col) => (
                    <div key={col.name} style={{ flex: col.width || 1 }}>
                      <FieldLabel>{col.title || col.name}</FieldLabel>
                      {renderColumnInput(col, row.values[col.name], (v) =>
                        updateMainValue(idx, col.name, v)
                      )}
                    </div>
                  ))}
              </EntryCardRow>

              {tierTwo.length > 0 && (
                <>
                  {row.nested_rows.map((nr, nIdx) => (
                    <EntryCardRow key={nIdx}>
                      {tierTwo
                        .filter((c) => c.type !== "remove_button")
                        .map((col) => (
                          <div key={col.name} style={{ flex: col.width || 1 }}>
                            <FieldLabel>{col.title || col.name}</FieldLabel>
                            {renderColumnInput(col, nr.values[col.name], (v) =>
                              updateNestedValue(idx, nIdx, col.name, v)
                            )}
                          </div>
                        ))}
                      {row.nested_rows.length > 1 && (
                        <RemoveButton
                          type="button"
                          onClick={() => removeNestedRow(idx, nIdx)}
                          aria-label="Remove nested row"
                        >
                          ✕
                        </RemoveButton>
                      )}
                    </EntryCardRow>
                  ))}
                  <AddButton type="button" onClick={() => addNestedRow(idx)}>
                    + Add detail
                  </AddButton>
                </>
              )}
            </NestedPanelBody>
          )}
        </NestedPanel>
      ))}
      <AddButton type="button" onClick={addMainRow}>
        + Add another
      </AddButton>
    </ChoiceList>
  );
};

export default MultiColumnNestedRenderer;
